import { supabase } from '../lib/supabase';

// Helper function to check schema errors
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = toCamelCase(obj?.[key]);
  }
  return camelObj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = toSnakeCase(obj?.[key]);
  }
  return snakeObj;
}

// Purchase Requests Service
export const purchaseRequestsService = {
  async getByBuyerId(buyerId) {
    try {
      const { data, error } = await supabase?.from('purchase_requests')?.select('*')?.eq('buyer_id', buyerId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get purchase requests error:', error);
      throw error;
    }
  },

  async create(requestData) {
    try {
      const snakeData = toSnakeCase(requestData);
      const { data, error } = await supabase?.from('purchase_requests')?.insert(snakeData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create purchase request error:', error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      const snakeUpdates = toSnakeCase(updates);
      const { data, error } = await supabase?.from('purchase_requests')?.update(snakeUpdates)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Update purchase request error:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('purchase_requests')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Delete purchase request error:', error);
      throw error;
    }
  },

  async getActiveRequests() {
    try {
      const { data, error } = await supabase?.from('purchase_requests')?.select(`
          *,
          buyer:buyer_id (
            id,
            full_name,
            avatar_url,
            is_verified,
            rating
          )
        `)?.eq('status', 'active')?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get active requests error:', error);
      throw error;
    }
  }
};

// Offers Service
export const offersService = {
  async getByRequestIds(requestIds) {
    try {
      const { data, error } = await supabase?.from('offers')?.select(`
          *,
          traveler:traveler_id (
            id,
            full_name,
            avatar_url,
            is_verified,
            rating,
            completed_trips
          )
        `)?.in('request_id', requestIds)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get offers error:', error);
      throw error;
    }
  },

  async acceptOffer(offerId, buyerId, travelerId, requestId) {
    try {
      // Create deal from accepted offer
      const { data, error } = await supabase?.from('deals')?.insert({
          offer_id: offerId,
          buyer_id: buyerId,
          traveler_id: travelerId,
          request_id: requestId,
          status: 'accepted'
        })?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Accept offer error:', error);
      throw error;
    }
  },

  async declineOffer(offerId) {
    try {
      const { error } = await supabase?.from('offers')?.delete()?.eq('id', offerId);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Decline offer error:', error);
      throw error;
    }
  },

  async getByTravelerId(travelerId) {
    try {
      const { data, error } = await supabase?.from('offers')?.select(`
          *,
          request:request_id (
            id,
            item_name,
            category,
            origin_country,
            destination_country,
            budget,
            delivery_date,
            carrier_fee,
            status
          )
        `)?.eq('traveler_id', travelerId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get traveler offers error:', error);
      throw error;
    }
  },

  async create(offerData) {
    try {
      const snakeData = toSnakeCase(offerData);
      const { data, error } = await supabase?.from('offers')?.insert(snakeData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create offer error:', error);
      throw error;
    }
  }
};

// Deals Service
export const dealsService = {
  async getById(dealId) {
    try {
      const { data, error } = await supabase?.from('deals')?.select(`
          *,
          buyer:buyer_id (
            id,
            full_name,
            avatar_url,
            is_verified,
            rating
          ),
          traveler:traveler_id (
            id,
            full_name,
            avatar_url,
            is_verified,
            rating,
            completed_trips
          ),
          request:request_id (
            id,
            item_name,
            category,
            origin_country,
            destination_country,
            budget,
            carrier_fee
          )
        `)?.eq('id', dealId)?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Get deal error:', error);
      throw error;
    }
  },

  async getByTravelerId(travelerId) {
    try {
      const { data, error } = await supabase?.from('deals')?.select(`
          *,
          traveler:traveler_id (
            id,
            full_name,
            avatar_url,
            is_verified,
            rating,
            completed_trips
          ),
          request:request_id (
            id,
            item_name,
            category,
            origin_country,
            destination_country,
            budget,
            carrier_fee,
            status
          )
        `)?.eq('traveler_id', travelerId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get traveler deals error:', error);
      throw error;
    }
  }
};

// Messages Service
export const messagesService = {
  async getByDealId(dealId) {
    try {
      const { data, error } = await supabase?.from('messages')?.select(`
          *,
          sender:sender_id (
            id,
            full_name,
            avatar_url
          )
        `)?.eq('deal_id', dealId)?.order('created_at', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get messages error:', error);
      throw error;
    }
  },

  async create(messageData) {
    try {
      const snakeData = toSnakeCase(messageData);
      const { data, error } = await supabase?.from('messages')?.insert(snakeData)?.select(`
          *,
          sender:sender_id (
            id,
            full_name,
            avatar_url
          )
        `)?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create message error:', error);
      throw error;
    }
  },

  subscribeToMessages(dealId, callback) {
    const channel = supabase?.channel(`messages_${dealId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `deal_id=eq.${dealId}`
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )?.subscribe();
    
    return channel;
  },

  unsubscribe(channel) {
    supabase?.removeChannel(channel);
  }
};

// Traveler Trips Service
export const travelerTripsService = {
  async getByTravelerId(travelerId) {
    try {
      const { data, error } = await supabase?.from('traveler_trips')?.select('*')?.eq('traveler_id', travelerId)?.eq('status', 'active')?.order('departure_date', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get traveler trips error:', error);
      throw error;
    }
  },

  async create(tripData) {
    try {
      const snakeData = toSnakeCase(tripData);
      const { data, error } = await supabase?.from('traveler_trips')?.insert(snakeData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create traveler trip error:', error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      const snakeUpdates = toSnakeCase(updates);
      const { data, error } = await supabase?.from('traveler_trips')?.update(snakeUpdates)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Update traveler trip error:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('traveler_trips')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Delete traveler trip error:', error);
      throw error;
    }
  }
};

// Payments Service
export const paymentsService = {
  async getByDealId(dealId) {
    try {
      const { data, error } = await supabase?.from('payments')?.select(`
          *,
          buyer:buyer_id (
            id,
            full_name,
            email,
            avatar_url
          ),
          traveler:traveler_id (
            id,
            full_name,
            email,
            avatar_url
          ),
          deal:deal_id (
            id,
            status,
            request:request_id (
              item_name,
              budget,
              carrier_fee
            )
          )
        `)?.eq('deal_id', dealId)?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return null;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Get payment error:', error);
      throw error;
    }
  },

  async getByUserId(userId) {
    try {
      const { data, error } = await supabase?.from('payments')?.select(`
          *,
          buyer:buyer_id (
            id,
            full_name,
            email
          ),
          traveler:traveler_id (
            id,
            full_name,
            email
          ),
          deal:deal_id (
            id,
            status,
            request:request_id (
              item_name,
              category,
              origin_country,
              destination_country
            )
          )
        `)?.or(`buyer_id.eq.${userId},traveler_id.eq.${userId}`)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get user payments error:', error);
      throw error;
    }
  },

  async getStatusHistory(paymentId) {
    try {
      const { data, error } = await supabase?.from('payment_status_history')?.select('*')?.eq('payment_id', paymentId)?.order('created_at', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get payment status history error:', error);
      throw error;
    }
  },

  async create(paymentData) {
    try {
      const snakeCaseData = toSnakeCase(paymentData);
      const { data, error } = await supabase?.from('payments')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        console.error('Create payment error:', error);
        throw error;
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create payment error:', error);
      throw error;
    }
  },

  async updateStatus(paymentId, newStatus, note = null) {
    try {
      const { error } = await supabase?.rpc('update_payment_status', {
        p_payment_id: paymentId,
        p_new_status: newStatus,
        p_note: note
      });
      
      if (error) {
        console.error('Update payment status error:', error);
        throw error;
      }
      
      return { success: true };
    } catch (error) {
      console.error('Update payment status error:', error);
      throw error;
    }
  },

  async update(paymentId, updates) {
    try {
      const snakeCaseUpdates = toSnakeCase(updates);
      const { data, error } = await supabase?.from('payments')?.update(snakeCaseUpdates)?.eq('id', paymentId)?.select()?.single();
      
      if (error) {
        console.error('Update payment error:', error);
        throw error;
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Update payment error:', error);
      throw error;
    }
  }
};

// Notifications Service
export const notificationsService = {
  async getByUserId(userId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(50);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get notifications error:', error);
      throw error;
    }
  },

  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('is_read', false);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return 0;
        }
      }
      
      return count || 0;
    } catch (error) {
      console.error('Get unread count error:', error);
      throw error;
    }
  },

  async markAsRead(notificationIds) {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.in('id', notificationIds);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Mark notifications as read error:', error);
      throw error;
    }
  },

  async markAllAsRead(userId) {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', userId)?.eq('is_read', false);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Mark all notifications as read error:', error);
      throw error;
    }
  },

  async delete(notificationId) {
    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  },

  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel(`notifications_${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )?.subscribe();
    
    return channel;
  },

  unsubscribe(channel) {
    supabase?.removeChannel(channel);
  }
};
